import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { ROUTES } from './routeConfig';

/**
 * ProtectedRoute
 *
 * Wraps routes that require authentication.
 * Redirects unauthenticated users to the sign-in page,
 * preserving the intended destination for post-login redirect.
 *
 * Integrates with:
 * - RequireEmailVerification: shows banner for unverified users
 * - RequireRole: enforces role-based access within protected routes
 *
 * @example
 * ```tsx
 * <Route element={<ProtectedRoute />}>
 *   <Route element={<RequireEmailVerification />}>
 *     <Route path="/dashboard" element={<DashboardPage />} />
 *   </Route>
 * </Route>
 * ```
 */
export const ProtectedRoute: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Show nothing while auth state is loading to prevent flash
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-dark-900">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-3 border-primary-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-dark-500 dark:text-dark-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to sign-in with the current location saved in state
    // so we can redirect back after successful authentication
    return <Navigate to={ROUTES.SIGN_IN} state={{ from: location }} replace />;
  }

  return <Outlet />;
};

ProtectedRoute.displayName = 'ProtectedRoute';